import { useRequest } from 'features/request/useRequest';
import { bimWorkOrderChapterLabel } from 'pages/Project/BimProject/WorkOrder/types';
import type { IListItem } from 'types/Contract';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimWorkOrderChangeChaptersDataGql } from '../workOrderChangeQueries';

export const useBimWorkOrderChangePriceCounter = ({
    workOrderChangeId,
    workOrderChangeRootObjectId = null,
    constructionTypeId,
    workOrderRootObjectId,
    workOrderSectionOrChapter,
}: {
    workOrderChangeId: number | undefined;
    workOrderChangeRootObjectId: number | null | undefined;
    constructionTypeId: number | undefined;
    workOrderRootObjectId: number | undefined;
    workOrderSectionOrChapter: IListItem<string> | undefined;
}) => {
    const dispatch = useDispatch();

    const isValidToDisplay = !!(
        workOrderChangeId &&
        constructionTypeId &&
        (workOrderRootObjectId || workOrderChangeRootObjectId)
    );

    const variables = useMemo(
        () => ({
            workOrderChangeId,
            constructionTypeId,
            workOrderRootObjectId,
            workOrderChangeRootObjectId,
        }),
        [constructionTypeId, workOrderChangeId, workOrderChangeRootObjectId, workOrderRootObjectId],
    );

    const [bimWorkOrderChangeData, , , bimWorkOrderChangeDataRequest] = useRequest({
        requestId: 'bimWorkOrderChangeChapterData',
        query: bimWorkOrderChangeChaptersDataGql,
        withEffect: isValidToDisplay,
        variables,
    });

    const refreshWorkOrderChangeChapterData = useCallback(() => {
        dispatch(bimWorkOrderChangeDataRequest(variables));
    }, [bimWorkOrderChangeDataRequest, dispatch, variables]);

    const priceListCounter = useMemo(
        () =>
            isValidToDisplay
                ? bimWorkOrderChangeData?.bimWorkOrderChangeChapterPriceCounts?.data?.find(
                      (i) => i?.chapter === workOrderSectionOrChapter?.id,
                  )?.pricesCount
                : undefined,
        [
            bimWorkOrderChangeData?.bimWorkOrderChangeChapterPriceCounts?.data,
            isValidToDisplay,
            workOrderSectionOrChapter?.id,
        ],
    );

    const chaptersTooltip = useMemo(() => {
        if (!bimWorkOrderChangeData?.bimWorkOrderChangeChapterPriceCounts?.data || !isValidToDisplay) {
            return undefined;
        }

        return bimWorkOrderChangeData?.bimWorkOrderChangeChapterPriceCounts?.data.map((item) => ({
            chapter: item?.chapter ? bimWorkOrderChapterLabel[item.chapter] : null,
            priceCount: item?.pricesCount,
        }));
    }, [bimWorkOrderChangeData?.bimWorkOrderChangeChapterPriceCounts?.data, isValidToDisplay]);

    const workOrderHasNoPrices = useMemo(
        () =>
            bimWorkOrderChangeData?.bimWorkOrderChangeChapterPriceCounts?.data?.every(
                (chapter) => chapter?.pricesCount === 0,
            ),
        [bimWorkOrderChangeData?.bimWorkOrderChangeChapterPriceCounts?.data],
    );

    return {
        priceListCounter,
        chaptersTooltip,
        refreshWorkOrderChangeChapterData,
        workOrderHasNoPrices,
    };
};
